import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { checkHubSession } from '@alascorporation/api-client'
import { api, storeSession } from '../lib/api'
import { env } from '../config/env'
import { AuthShell } from '../components/AuthShell'

export function AuthorizePage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [error, setError] = useState('')

  const clientId = searchParams.get('client_id')
  const redirectUri = searchParams.get('redirect_uri')

  useEffect(() => {
    if (!clientId || !redirectUri) {
      setError('Solicitud de autorización inválida: faltan parámetros.')
      return
    }

    let cancelled = false

    async function ensureSession(): Promise<boolean> {
      // Always defer to the shared hub cookie, never to hasValidSession()
      // alone — a logout on another *.satelab.org app revokes that cookie
      // server-side but can't reach id.satelab.org's own localStorage, so
      // trusting a lingering local token here would silently re-authorize
      // a session the user already ended elsewhere. Same rule LoginPage
      // already follows.
      const shared = await checkHubSession(env.apiUrl)
      if (cancelled) return false
      if (shared) {
        storeSession(shared.user, shared.token)
        return true
      }
      return false
    }

    async function authorize() {
      const authenticated = await ensureSession()
      if (cancelled) return

      if (!authenticated) {
        navigate(`/login?${searchParams.toString()}`, { replace: true })
        return
      }

      try {
        const { data } = await api.get('/auth/sso/authorize', {
          params: {
            client_id: clientId,
            redirect_uri: redirectUri,
            code_challenge: searchParams.get('code_challenge') ?? undefined,
            code_challenge_method: searchParams.get('code_challenge_method') ?? undefined,
            state: searchParams.get('state') ?? undefined,
          },
        })
        if (!cancelled) {
          window.location.href = data.data.redirect
        }
      } catch (err) {
        if (cancelled) return
        const status = (err as { response?: { status?: number } }).response?.status
        if (status === 401) {
          navigate(`/login?${searchParams.toString()}`, { replace: true })
        } else {
          setError('No se pudo completar la autorización. Verifica que la aplicación esté registrada correctamente.')
        }
      }
    }

    authorize()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, redirectUri])

  if (error) {
    return (
      <AuthShell>
        <div className="auth-card">
          <p className="auth-eyebrow">SateLab</p>
          <h1 className="auth-title">No se pudo autorizar</h1>
          <p className="auth-subtitle">{error}</p>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell>
      <div className="auth-card">
        <p className="auth-eyebrow">SateLab</p>
        <div className="auth-loading">
          <span className="auth-spinner" aria-hidden="true" />
          Redirigiendo...
        </div>
      </div>
    </AuthShell>
  )
}
